import {
  useCallback, useEffect, useRef, useState, type ReactNode,
} from 'react';
import { api, hasSession, onAuthChange, type Pad, type User } from './lib/api';
import AuthScreen from './ui/AuthScreen';
import Lobby from './ui/Lobby';
import PadScreen from './ui/PadScreen';
import {
  bindBackButton, exitApp, hideSplash, onDeepLink, setStatusBarTheme,
} from './lib/native';

type Route =
  | { name: 'loading' }
  | { name: 'auth' }
  | { name: 'lobby' }
  | { name: 'pad'; pad: Pad; ticket: string; role: string };

interface DeepLink {
  reset?: string;
  verify?: string;
  join?: string;
}

/** Parse the tokens we care about out of the hash, then leave the URL clean. */
function readHash(): DeepLink {
  const hash = window.location.hash.replace(/^#\/?/, '');
  const [path, query] = hash.split('?');
  const params = new URLSearchParams(query ?? '');
  if (path === 'reset') return { reset: params.get('token') ?? '' };
  if (path === 'verify') return { verify: params.get('token') ?? '' };
  const joinMatch = /^join\/([A-Za-z0-9]{6})$/.exec(path);
  if (joinMatch) return { join: joinMatch[1].toUpperCase() };
  return {};
}

export default function App() {
  const [route, setRoute] = useState<Route>({ name: 'loading' });
  // The back-button handler is bound once and must not close over a stale
  // route, so it reads the current one through a ref.
  const routeRef = useRef(route);
  routeRef.current = route;
  const [exitHint, setExitHint] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [deepLink, setDeepLink] = useState<DeepLink>(() => readHash());

  const clearHash = useCallback(() => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  // Restore the session on load. A reset link always wins, since the whole
  // point is to get in when the stored session is no longer any use.
  useEffect(() => {
    let cancelled = false;
    if (deepLink.reset !== undefined || deepLink.verify !== undefined) {
      setRoute({ name: 'auth' });
      return () => { cancelled = true; };
    }
    if (!hasSession()) {
      setRoute({ name: 'auth' });
      return () => { cancelled = true; };
    }
    api.me()
      .then((res) => {
        if (cancelled) return;
        setUser(res.user);
        setRoute({ name: 'lobby' });
      })
      .catch(() => {
        if (!cancelled) setRoute({ name: 'auth' });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the session is dropped anywhere (expired refresh token), come back here.
  useEffect(() => onAuthChange((signedIn) => {
    if (!signedIn) {
      setUser(null);
      setRoute({ name: 'auth' });
    }
  }), []);

  useEffect(() => {
    const onHashChange = () => setDeepLink(readHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Personal preferences carry the theme, but the sign-in screens need one too.
  useEffect(() => {
    let theme = 'light';
    try {
      const raw = localStorage.getItem('ink-share.prefs');
      theme = raw && JSON.parse(raw).theme === 'dark' ? 'dark' : 'light';
    } catch {
      theme = 'light';
    }
    document.documentElement.dataset.theme = theme;
    // The system bars follow the paper the user picked, not the OS setting.
    setStatusBarTheme(theme === 'dark');
  }, [route.name]);

  // --- native shell ---------------------------------------------------------

  useEffect(() => {
    hideSplash();
  }, []);

  /**
   * Android's back button. Modals and popovers register their own handlers as
   * they open, so by the time a press reaches here the user is on a bare
   * screen. From a pad that means "leave the pad"; from the top level it means
   * they want out, and Android expects the app to close rather than sit there.
   * The double-press is so a stray swipe never loses a session.
   */
  const lastBackRef = useRef(0);
  useEffect(() => {
    bindBackButton(() => {
      if (routeRef.current.name === 'pad') {
        setRoute({ name: 'lobby' });
        return;
      }
      const now = Date.now();
      if (now - lastBackRef.current < 2000) {
        exitApp();
        return;
      }
      lastBackRef.current = now;
      setExitHint(true);
      window.setTimeout(() => setExitHint(false), 2000);
    });
  }, []);

  // A join link tapped outside the app arrives here rather than as a hash.
  useEffect(() => onDeepLink((url) => {
    const hash = url.includes('#') ? url.slice(url.indexOf('#')) : '';
    if (!hash) return;
    window.location.hash = hash;
    setDeepLink(readHash());
  }), []);

  const signOut = useCallback(async () => {
    await api.logout();
    setUser(null);
    setRoute({ name: 'auth' });
  }, []);

  // "Press back again to close" — the standard Android affordance, so a single
  // stray press never drops someone out of the app.
  const withExitHint = (screen: ReactNode) => (
    <>
      {screen}
      {exitHint ? (
        <div className="toasts" role="status" aria-live="polite">
          <div className="toast">Press back again to close Ink Share</div>
        </div>
      ) : null}
    </>
  );

  if (route.name === 'loading') {
    return (
      <div className="screen">
        <span className="spinner" />
      </div>
    );
  }

  if (route.name === 'auth' || !user) {
    const link = deepLink.reset !== undefined
      ? { mode: 'reset' as const, token: deepLink.reset }
      : deepLink.verify !== undefined
        ? { mode: 'verify' as const, token: deepLink.verify }
        : null;
    return withExitHint(
      <AuthScreen
        deepLink={link}
        onDeepLinkConsumed={() => { setDeepLink((prev) => ({ join: prev.join })); clearHash(); }}
        onSignedIn={(signedIn) => {
          setUser(signedIn);
          setRoute({ name: 'lobby' });
        }}
      />,
    );
  }

  if (route.name === 'pad') {
    return (
      <PadScreen
        key={route.ticket}
        user={user}
        pad={route.pad}
        ticket={route.ticket}
        role={route.role}
        onLeave={() => setRoute({ name: 'lobby' })}
      />
    );
  }

  return withExitHint(
    <Lobby
      user={user}
      pendingCode={deepLink.join ?? null}
      onPendingCodeConsumed={() => { setDeepLink((prev) => ({ ...prev, join: undefined })); clearHash(); }}
      onEnterPad={(pad, ticket, role) => setRoute({ name: 'pad', pad, ticket, role })}
      onSignOut={signOut}
      onUserChange={setUser}
    />,
  );
}
